import { useState, useCallback } from 'react';
import { useBoolean } from './useBoolean';

interface UseDailyResourceProps<T> {
    onMove: (fromLineId: string, itemId: string, toLineId: string) => Promise<void>;
    onDelete: (itemId: string) => Promise<void>;
    onDuplicate: (item: T, activityId: string) => Promise<void>;
}

export const useDailyResource = <T>(props: UseDailyResourceProps<T>) => {
    const [selectedItem, setSelectedItem] = useState<T | null>(null);
    const manageModal = useBoolean(false);

    const handleMove = useCallback(
        async (fromLineId: string, itemId: string, toLineId: string) => {
            if (fromLineId === toLineId) return;
            try {
                await props.onMove(fromLineId, itemId, toLineId);
            } catch (error) {
                console.error('Error moving item:', error);
            }
        },
        [props]
    );

    const handleDelete = useCallback(
        async (id: string) => {
            try {
                await props.onDelete(id);
            } catch (error) {
                console.error('Error deleting item:', error);
            }
        },
        [props]
    );

    const handleDuplicate = useCallback(
        async (item: T) => {
            try {
                await props.onDuplicate(item, '');
            } catch (error) {
                console.error('Error duplicating item:', error);
            }
        },
        [props]
    );

    const openModal = useCallback(
        (item: T | null) => {
            setSelectedItem(item);
            manageModal.setTrue();
        },
        [manageModal]
    );

    return {
        selectedItem,
        manageModal,
        handleMove,
        handleDelete,
        handleDuplicate,
        openModal,
    };
};
